import { useState } from "react";
import { Upload, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useSite } from "../../context/SiteContext";

function fileToDataUrl(file, maxW) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxW / img.width);
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "#fff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", 0.82));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function GalleryManager() {
  const { gallery, reload } = useSite();
  const [busy, setBusy] = useState(false);

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      for (const f of files) {
        const image = await fileToDataUrl(f, 1400);
        await api.create("gallery", { image, caption: "" });
      }
      toast.success(`${files.length} photo(s) added`);
      await reload();
    } catch { toast.error("Upload failed"); }
    finally { setBusy(false); e.target.value = ""; }
  };

  const saveCaption = async (item, caption) => {
    if (caption === item.caption) return;
    try { await api.update("gallery", item.id, { image: item.image, caption }); await reload(); }
    catch { toast.error("Failed to save caption"); }
  };

  const remove = async (id) => {
    try { await api.remove("gallery", id); toast.success("Photo removed"); await reload(); }
    catch { toast.error("Failed to delete"); }
  };

  return (
    <div data-testid="gallery-manager">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <h2 className="font-serif text-2xl text-forest">{gallery.length} Gallery Photo{gallery.length !== 1 ? "s" : ""}</h2>
        <label className="inline-flex items-center gap-2 bg-gold text-forest px-6 py-3 rounded-full text-sm uppercase tracking-widest hover:-translate-y-0.5 transition-transform cursor-pointer">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} Add Photos
          <input data-testid="upload-gallery" type="file" accept="image/*" multiple onChange={onUpload} className="hidden" disabled={busy} />
        </label>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {gallery.map((g) => (
          <div key={g.id || g.image} data-testid="gallery-admin-item" className="bg-white border border-line rounded-sm overflow-hidden">
            <div className="aspect-square overflow-hidden bg-bone">
              <img src={g.image} alt={g.caption || "gallery"} className="w-full h-full object-cover" />
            </div>
            <div className="p-3 flex items-center gap-2">
              <input
                data-testid="gallery-caption-input"
                defaultValue={g.caption}
                placeholder="Caption…"
                onBlur={(e) => saveCaption(g, e.target.value)}
                className="flex-1 min-w-0 text-sm bg-transparent border-b border-line focus:border-gold outline-none py-1"
              />
              <button data-testid="delete-gallery-btn" onClick={() => remove(g.id)} className="w-8 h-8 flex items-center justify-center border border-line rounded-sm hover:bg-destructive hover:text-white transition-colors shrink-0"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
        {gallery.length === 0 && <p className="p-8 text-muted col-span-full">No photos yet. Add event pictures above.</p>}
      </div>
    </div>
  );
}
